<template>
  <PageSection font="medium">
    <SectionGroup gap="medium">
      <SectionBox>
        <BasicSection appearance="list-page-header">
          <PageTitle appearance="mobile-row" title="Эксперты">
            <BasicLink
              target="_blank"
              appearance="question"
              href="/info/experts"
              class="experts-list-page__faq"
            >
              <span class="icon icon-question" />
            </BasicLink>
          </PageTitle>

          <SearchPanel
            :indicator-visible="hasCustomizedFilters"
            @openFilters="openModalFilters"
            appearance="new-search-panel"
          >
            <template #primary>
              <SearchInput
                v-model="state.query"
                @update:modelValue="scheduleListUpdate"
                type="search"
                placeholder="Поиск по навыкам, целям, имени"
              />
            </template>
            <template #secondary>
              <DropdownSelect
                v-model="state.order"
                @input="scheduleListUpdate"
                :options="filters.options.order"
                full-width
              />
            </template>
          </SearchPanel>

          <TransitionExpand v-if="!mediaQueries.mobile">
            <div
              v-if="hasCustomizedFilters"
              :key="hasCustomizedFilters"
              class="experts-list-page__filters"
            >
              <SectionGroup gap="medium">
                <SelectedFilters
                  :items="filters.selectedList"
                  title="Фильтры:"
                  :max="2"
                  @remove="filters.resetOne($event), scheduleListUpdate()"
                />

                <FiltersControls
                  appearance="search-panel"
                  no-dashed-reset
                  @reset="filters.reset(), scheduleListUpdate()"
                />
              </SectionGroup>
            </div>
          </TransitionExpand>

          <TransitionExpand>
            <SearchTotal
              v-if="currentExperts.list.length !== 0"
              appearance="new-search-panel"
              v-model="searchResultsTitle"
            />
          </TransitionExpand>
        </BasicSection>
      </SectionBox>

      <div class="mq-mobile-only">
        <TransitionExpand>
          <ExpertBillingBanner
            v-if="showBilling"
            @closeBanner="closeBillingBanner"
          />
          <ExpertsFeedbackBlock
            v-else-if="showExpertsFeedBackBlock"
            @hide="showFeedback = false"
          />
        </TransitionExpand>
      </div>

      <ExpertScoreBlock
        v-if="mediaQueries.mobile && expertConnections.length"
        :experts="expertConnections"
      />

      <ResourceAction
        ref="modalFiltersAction"
        :action="action"
        :debounce="400"
        @success="onModalRequestSuccess"
        v-slot="{ isLoading: isModalLoading }"
      >
        <PaginatedResource
          ref="listResource"
          v-model="currentExperts"
          :action="action"
          :params="params"
          :debounce="400"
          @success="onListRequestSuccess"
          v-slot="{ isLoading }"
        >
          <TransitionExpand>
            <WithPagination
              scroll-to-top
              :params="mappedParams"
              :key="isLoading"
            >
              <template v-if="isLoading || isModalLoading">
                <SectionBox
                  v-for="(_, index) in Array(
                    currentExperts.list.length < 3
                      ? 3
                      : currentExperts.list.length,
                  )"
                  :key="index"
                >
                  <BasicSection appearance="same-paddings">
                    <ExpertCardSkeleton />
                  </BasicSection>
                </SectionBox>
              </template>
              <SectionBox v-else-if="!currentExperts.meta.total">
                <NoContent v-bind="$options.noContent" />
              </SectionBox>
              <template v-else>
                <ExpertCard
                  v-for="expert in currentExperts.list"
                  :key="expert.id"
                  :expert="expert"
                  :user="visitor"
                  :connections="activeConnections"
                />
              </template>
            </WithPagination>
          </TransitionExpand>
        </PaginatedResource>
      </ResourceAction>
    </SectionGroup>

    <VuePortal target="[data-list-sidebar]">
      <ListPageSidebar v-if="!mediaQueries.mobile">
        <template #beforeFilters>
          <TransitionExpand>
            <ExpertBillingBanner
              v-if="showBilling"
              @closeBanner="closeBillingBanner"
            />
            <ExpertsFeedbackBlock
              v-else-if="showExpertsFeedBackBlock"
              @hide="showFeedback = false"
            />
          </TransitionExpand>
          <TransitionExpand>
            <ExpertScoreBlock
              v-if="expertConnections.length"
              :experts="expertConnections"
              sidebar
            />
          </TransitionExpand>
        </template>
        <template #filters>
          <ExpertFilters
            :filters="filters.items"
            :options="filters.options"
            :dynamic-options="filters.dynamicOptions"
            @change="scheduleListUpdate"
          >
            <template #controls>
              <FiltersControls
                v-if="hasCustomizedFilters"
                @reset="filters.reset(), scheduleListUpdate()"
              />
            </template>
          </ExpertFilters>
        </template>
      </ListPageSidebar>
    </VuePortal>

    <TransitionFade body-scroll-lock>
      <FiltersModal v-if="filtersModalVisible" @close="closeModalFilters">
        <template #filters="{ filters: items, options, dynamicOptions }">
          <ExpertFilters
            :filters="items"
            :options="options"
            :dynamic-options="dynamicOptions"
            @change="onModalFiltersUpdate"
          />
        </template>

        <template #submit-button="{ filters, dynamicOptions }">
          <BasicButton
            @click="applyModalFilters({ filters, dynamicOptions })"
            appearance="primary"
            full-width
            size="xl"
            >{{ applyFiltersTitle }}</BasicButton
          >
        </template>
      </FiltersModal>
    </TransitionFade>
  </PageSection>
</template>

<script>
import { ExpertsService } from 'src/services/experts.js';
import {
  getFormattedNumber,
  getRussianPluralDeclension,
} from 'src/utils/translation.js';

import BasicButton from 'src/components/BasicButton/BasicButton.vue';
import BasicLink from 'src/components/BasicLink/BasicLink.vue';
import ExpertBillingBanner from 'src/components/experts/ExpertBillingBanner/ExpertBillingBanner.vue';
import ExpertCard from 'src/components/experts/ExpertCard/ExpertCard.vue';
import ExpertCardSkeleton from 'src/components/experts/ExpertCardSkeleton/ExpertCardSkeleton.vue';
import ExpertFilters from 'src/components/experts/ExpertFilters/ExpertFilters.vue';
import ExpertScoreBlock from 'src/components/experts/ExpertScoreBlock/ExpertScoreBlock.vue';
import ExpertsFeedbackBlock from 'src/components/experts/ExpertsFeedbackBlock/ExpertsFeedbackBlock.vue';
import FiltersControls from 'src/components/filters/FiltersControls/FiltersControls.vue';
import FiltersModal from 'src/components/filters/FiltersModal/FiltersModal.vue';
import SelectedFilters from 'src/components/filters/SelectedFilters/SelectedFilters.vue';
import DropdownSelect from 'src/components/form/DropdownSelect/DropdownSelect.vue';
import SearchInput from 'src/components/form/SearchInput/SearchInput.vue';
import ListPageSidebar from 'src/components/ListPageSidebar/ListPageSidebar.vue';
import PageTitle from 'src/components/PageTitle/PageTitle.vue';
import PaginatedResource from 'src/components/pagination/PaginatedResource/PaginatedResource.vue';
import WithPagination from 'src/components/pagination/WithPagination/WithPagination.vue';
import NoContent from 'src/components/placeholders/NoContent/NoContent.vue';
import ResourceAction from 'src/components/ResourceAction/ResourceAction.vue';
import SearchPanel from 'src/components/search/SearchPanel/SearchPanel.vue';
import SearchTotal from 'src/components/SearchTotal/SearchTotal.vue';
import SectionBox from 'src/components/SectionBox/SectionBox.vue';
import BasicSection from 'src/components/sections/BasicSection/BasicSection.vue';
import PageSection from 'src/components/sections/PageSection/PageSection.vue';
import SectionGroup from 'src/components/sections/SectionGroup/SectionGroup.vue';
import TransitionExpand from 'src/components/transitions/TransitionExpand/TransitionExpand.vue';
import TransitionFade from 'src/components/transitions/TransitionFade/TransitionFade.vue';
import VuePortal from 'src/components/VuePortal/VuePortal.vue';

import noContentIconPath from 'images/no_content.icon.vacancies.svg';

const expertsDeclension = getRussianPluralDeclension(
  'Найден ${0} эксперт',
  'Найдено ${0} эксперта',
  'Найдено ${0} экспертов',
);

const unauthExpertsDeclension = getRussianPluralDeclension(
  'Найден ${0} эксперт, еще ${1} после <a href=${2}>авторизации</a>',
  'Найдено ${0} эксперта, еще ${1} после <a href=${2}>авторизации</a>',
  'Найдено ${0} экспертов, еще ${1} после <a href=${2}>авторизации</a>',
);

const submitButtonDeclension = getRussianPluralDeclension(
  'Показать ${0} эксперта',
  'Показать ${0} эксперта',
  'Показать ${0} экспертов',
);

export default {
  name: 'ExpertsListPage',
  components: {
    BasicLink,
    SearchInput,
    ResourceAction,
    ExpertsFeedbackBlock,
    ExpertBillingBanner,
    ExpertScoreBlock,
    DropdownSelect,
    ExpertCardSkeleton,
    NoContent,
    WithPagination,
    PaginatedResource,
    ExpertFilters,
    BasicButton,
    FiltersModal,
    TransitionFade,
    ListPageSidebar,
    VuePortal,
    SearchTotal,
    FiltersControls,
    SelectedFilters,
    TransitionExpand,
    SearchPanel,
    PageTitle,
    PageSection,
    ExpertCard,
    BasicSection,
    SectionBox,
    SectionGroup,
  },
  inject: ['mediaQueries', 'visitor', 'filters'],
  props: {
    experts: {
      type: Object,
      required: true,
    },
    query: {
      type: String,
      default: '',
    },
    order: {
      type: String,
      required: true,
    },
    expertConnections: {
      type: Array,
      required: true,
    },
    activeConnections: {
      type: Array,
      required: true,
    },
    showFeedbackBlock: Boolean,
    showBillingBanner: Boolean,
  },
  noContent: {
    title: 'Эксперты не найдены',
    description:
      'Мы не нашли экспертов по заданным параметрам. Попробуйте изменить условия поиска.',
    icon: {
      src: noContentIconPath,
      width: '97px',
      height: '97px',
    },
  },
  data() {
    const {
      experts,
      query,
      order,
      showFeedbackBlock,
      showBillingBanner,
    } = this;

    return {
      state: {
        query,
        order,
      },
      currentExperts: experts,
      modalExperts: null,
      filtersModalVisible: false,
      showFeedback: showFeedbackBlock,
      showBilling: showBillingBanner,
    };
  },
  methods: {
    action: ExpertsService.getList,
    scheduleListUpdate() {
      this.$refs.listResource.loadAndReplace(this.params);
    },
    onListRequestSuccess({ params }) {
      ExpertsService.syncLocation(params);
    },
    openModalFilters() {
      this.filtersModalVisible = true;
      this.modalExperts = this.currentExperts;
    },
    closeModalFilters() {
      this.$refs.modalFiltersAction.cancel();
      this.filtersModalVisible = false;
    },
    onModalFiltersUpdate(filters) {
      this.$refs.modalFiltersAction.dispatch({
        ...this.baseParams,
        ...filters,
      });
    },
    onModalRequestSuccess({ data }) {
      if (this.filtersModalVisible) {
        this.modalExperts = data;
      } else {
        this.currentExperts = data;
      }
    },
    applyModalFilters({ filters, dynamicOptions }) {
      this.filtersModalVisible = false;
      this.filters.set({ filters, dynamicOptions });
      this.currentExperts = this.modalExperts;
      const params = { ...this.baseParams, ...filters };
      ExpertsService.syncLocation(params);
    },
    closeBillingBanner() {
      this.showBilling = false;
      ExpertsService.closeBillingBanner();
    },
  },
  computed: {
    showExpertsFeedBackBlock() {
      return (
        !this.visitor.isGuest &&
        this.showFeedback &&
        !this.expertConnections.length
      );
    },
    baseParams() {
      const { state } = this;
      const { query, order } = state;
      return { query, order };
    },
    params() {
      const params = { ...this.baseParams, ...this.filters.items };

      if (!params.rate) {
        delete params.currencies;
      }

      return params;
    },
    searchResultsTitle() {
      const value = getFormattedNumber(this.currentExperts.meta.total);

      const afterAuthExpertsCount = getFormattedNumber(
        this.currentExperts.meta.afterAuthExpertsCount,
      );

      if (afterAuthExpertsCount === 0 || !this.visitor.isGuest) {
        return expertsDeclension(value).replace('${0}', value);
      }

      return unauthExpertsDeclension(value)
        .replace('${0}', value)
        .replace('${1}', afterAuthExpertsCount)
        .replace('${2}', this.currentExperts.meta.authorizeUrl);
    },
    hasCustomizedFilters() {
      return this.filters.selectedList.length !== 0;
    },
    applyFiltersTitle() {
      if (!this.modalExperts) return null;

      const value = this.modalExperts.meta.total;

      return submitButtonDeclension(value).replace('${0}', value);
    },
    searchFieldsButtonTitle() {
      const fields = this.filters.items.searchFields;
      if (fields.length !== 0) {
        return `Искать ${fields
          .map(
            (searchField) =>
              this.filters.options.searchFields.find(
                (item) => item.value === searchField,
              ).title,
          )
          .join(', ')}`;
      }
      return 'Искать везде';
    },
    mappedParams() {
      return ExpertsService.getMappedParams(this.params);
    },
  },
};
</script>
